import React, { useEffect, useState } from "react";
import { UserPlus, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { payFranchiseOrder } from "@/lib/franchisePay";
import { PageHeader, EmptyState, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function EnrollDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [f, setF] = useState({ student_name: "", student_email: "", student_mobile: "", course_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/courses").then(({ data }) => setCourses((data.items || data).filter((c) => (c.discount_price || c.price) > 0))); }, [open]);
  const course = courses.find((c) => c.id === f.course_id);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const go = async () => {
    if (!f.student_name.trim() || !f.student_email.trim() || !f.course_id) return toast.error("Fill student name, email and course");
    setBusy(true);
    try {
      const { data } = await api.post("/franchise/institute/enroll", f);
      setOpen(false);
      const st = await payFranchiseOrder(data, { description: `Enrollment: ${course.title}` });
      if (st === "paid") toast.success(`${f.student_name} is enrolled. They can sign in with “Forgot password” using ${f.student_email} if they are new.`);
      if (st === "processing") toast.info("Payment received — the enrollment appears once Razorpay confirms it.");
      setF({ student_name: "", student_email: "", student_mobile: "", course_id: "" });
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="viriks-amber text-white" data-testid="inst-enroll-btn"><UserPlus size={16} className="mr-1" /> Enroll student</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Enroll a student</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Student name *</Label><Input value={f.student_name} onChange={(e) => set("student_name", e.target.value)} data-testid="inst-enroll-name" /></div>
          <div><Label>Student email *</Label><Input type="email" value={f.student_email} onChange={(e) => set("student_email", e.target.value)} data-testid="inst-enroll-email" /></div>
          <div><Label>Mobile</Label><Input value={f.student_mobile} onChange={(e) => set("student_mobile", e.target.value)} data-testid="inst-enroll-mobile" /></div>
          <div><Label>Course *</Label>
            <Select value={f.course_id} onValueChange={(v) => set("course_id", v)}>
              <SelectTrigger data-testid="inst-enroll-course"><SelectValue placeholder="Select course" /></SelectTrigger>
              <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title} — ₹{(c.discount_price || c.price).toLocaleString("en-IN")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {course && <p className="text-sm text-muted-foreground">You pay <Money amount={course.discount_price || course.price} className="font-semibold text-navy" /> now. The commission on it is recorded in your ledger.</p>}
        </div>
        <DialogFooter><Button disabled={busy} onClick={go} className="viriks-navy text-white" data-testid="inst-enroll-pay">{busy ? "Please wait..." : "Continue to payment"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InstituteStudents() {
  const [list, setList] = useState(null);
  const load = () => api.get("/franchise/institute/students").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="institute-students">
      <PageHeader title="Students" subtitle="Students linked to your institute and their enrollments" action={<EnrollDialog onDone={load} />} />
      {list && list.length === 0 ? <EmptyState icon={Users} title="No students yet" subtitle="Share your institute link or enroll a student directly." /> : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          {(list || []).map((s) => (
            <div key={s.id} className="grid md:grid-cols-[220px_1fr] gap-3 px-5 py-4 border-b border-border last:border-0" data-testid={`inst-student-${s.id}`}>
              <div className="min-w-0"><div className="font-semibold text-navy truncate">{s.name}</div><div className="text-xs text-muted-foreground truncate">{s.email}</div></div>
              <div className="flex flex-wrap gap-2">
                {s.enrollments.length === 0 ? <span className="text-sm text-muted-foreground">Not enrolled in any course yet</span> : s.enrollments.map((e) => {
                  const expired = e.expires_at && new Date(e.expires_at) < new Date();
                  return <span key={e.id} className="text-xs border border-border rounded-full px-3 py-1">{e.course_title} • {e.progress || 0}% • <span className={expired ? "text-crimson" : "text-emerald-600"}>{expired ? "Expired" : "Active"}</span></span>;
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
